import { Api } from '@/api/api.client';
import type { Components } from '@/api/generated/api';
import { assertNotNullOrUndefined } from '@/utils/assertion.utils';

export async function fetchSupportMeasureEntry(params: {
  id: number;
}): Promise<Components.Schemas.SupportMeasureEntryDto> {
  const res = await Api.Routes.SupportMeasureEntry.retrieveById({ id: params.id });
  return res.data;
}

// current members + former members still referenced by protocol/activities (isRemoved), for timeline filtering
export async function fetchSupportMeasureEntryStudents(params: {
  id: number;
}): Promise<Components.Schemas.SupportMeasureFilterStudentDto[]> {
  const res = await Api.Routes.SupportMeasureEntry.retrieveStudents({ id: params.id });
  return res.data;
}

export async function createSupportMeasureEntry(
  dto: Components.Schemas.CreateSupportMeasureEntryDto,
): Promise<Components.Schemas.SupportMeasureEntryDto> {
  const res = await Api.Routes.SupportMeasureEntry.create(null, dto);
  return res.data;
}

export async function updateSupportMeasureEntry(params: {
  id: number;
  dto: Components.Schemas.UpdateSupportMeasureEntryDto;
}): Promise<Components.Schemas.SupportMeasureEntryDto> {
  const res = await Api.Routes.SupportMeasureEntry.update({ id: params.id }, params.dto);
  return res.data;
}

export async function deleteSupportMeasureEntry(params: { id: number }): Promise<void> {
  await Api.Routes.SupportMeasureEntry.delete({ id: params.id });
}

export async function addCompetencesToSupportMeasureEntry(params: {
  id: number;
  competenceIds: number[];
}): Promise<Components.Schemas.SupportMeasureEntryDto> {
  const res = await Api.Routes.SupportMeasureEntry.addCompetences(
    { id: params.id },
    { competenceIds: params.competenceIds },
  );
  return res.data;
}

export async function removeCompetenceFromSupportMeasureEntry(params: {
  id: number;
  competenceId: number;
}): Promise<Components.Schemas.SupportMeasureEntryDto> {
  const res = await Api.Routes.SupportMeasureEntry.removeCompetence({
    id: params.id,
    competenceId: params.competenceId,
  });
  return res.data;
}

export async function addStudentsToSupportMeasureEntry(params: {
  id: number;
  studentIds: number[];
}): Promise<Components.Schemas.SupportMeasureEntryDto> {
  const res = await Api.Routes.SupportMeasureEntry.addStudents({ id: params.id }, { studentIds: params.studentIds });
  return res.data;
}

export async function removeStudentFromSupportMeasureEntry(params: {
  id: number;
  studentId: number;
}): Promise<Components.Schemas.SupportMeasureEntryDto> {
  const res = await Api.Routes.SupportMeasureEntry.removeStudent({ id: params.id, studentId: params.studentId });
  return res.data;
}

// fully removes a student and erases their documentation (protocol participation + observations, and any
// protocol entry they were the sole participant of); records a `studentEntriesRemoved` activity
export async function removeStudentFinallyFromSupportMeasureEntry(params: {
  id: number;
  studentId: number;
}): Promise<Components.Schemas.SupportMeasureEntryDto> {
  const res = await Api.Routes.SupportMeasureEntry.deleteStudent({
    id: params.id,
    studentId: params.studentId,
  });
  return res.data;
}

export async function setSupportMeasureEntryStudents(params: {
  id: number;
  studentIds: number[];
}): Promise<Components.Schemas.SupportMeasureEntryDto> {
  const [firstStudentId, ...restStudentIds] = params.studentIds;
  assertNotNullOrUndefined(firstStudentId);
  const res = await Api.Routes.SupportMeasureEntry.setStudents(
    { id: params.id },
    { studentIds: [firstStudentId, ...restStudentIds] },
  );
  return res.data;
}

export async function setSupportMeasureEntryCompetences(params: {
  id: number;
  competenceIds: number[];
}): Promise<Components.Schemas.SupportMeasureEntryDto> {
  const res = await Api.Routes.SupportMeasureEntry.setCompetences(
    { id: params.id },
    { competenceIds: params.competenceIds },
  );
  return res.data;
}

export async function createSupportMeasureProtocolEntry(params: {
  id: number;
  dto: Components.Schemas.CreateSupportMeasureProtocolEntryDto;
}): Promise<Components.Schemas.SupportMeasureEntryDto> {
  const res = await Api.Routes.SupportMeasureEntry.createProtocolEntry({ id: params.id }, params.dto);
  return res.data;
}

export async function updateSupportMeasureProtocolEntry(params: {
  protocolEntryId: number;
  dto: Components.Schemas.UpdateSupportMeasureProtocolEntryDto;
}): Promise<Components.Schemas.SupportMeasureEntryDto> {
  const res = await Api.Routes.SupportMeasureEntry.updateProtocolEntry(
    { protocolEntryId: params.protocolEntryId },
    params.dto,
  );
  return res.data;
}

export async function deleteSupportMeasureProtocolEntry(params: {
  protocolEntryId: number;
}): Promise<Components.Schemas.SupportMeasureEntryDto> {
  const res = await Api.Routes.SupportMeasureEntry.deleteProtocolEntry({ protocolEntryId: params.protocolEntryId });
  return res.data;
}

export async function setSupportMeasureProtocolEntryRating(params: {
  protocolEntryId: number;
  rating: Components.Schemas.SupportMeasureProtocolEntryRating | null;
}): Promise<Components.Schemas.SupportMeasureEntryDto> {
  const res = await Api.Routes.SupportMeasureEntry.setProtocolEntryRating(
    { protocolEntryId: params.protocolEntryId },
    { rating: params.rating },
  );
  return res.data;
}

export async function startSupportMeasureReportPdfExport(params: {
  id: number;
  dto: Components.Schemas.StartSupportMeasureReportPdfDto;
}): Promise<Components.Schemas.AsyncOperationCreatedResponseDto> {
  return Api.Routes.SupportMeasureEntry.startPdfExport({ id: params.id }, params.dto);
}

// read by the headless print page; students default to the current members of the measure
export async function fetchSupportMeasureReportPdfData(params: {
  id: number;
  studentIds?: number[];
}): Promise<Components.Schemas.SupportMeasureReportPdfDataDto> {
  const res = await Api.Routes.SupportMeasureEntry.retrieveReportPdfData({
    id: params.id,
    studentIds: params.studentIds,
  });
  return res.data;
}

export async function addMaterialsToSupportMeasureEntry(params: {
  id: number;
  externalMaterialIds: number[];
}): Promise<Components.Schemas.SupportMeasureEntryDto> {
  const res = await Api.Routes.SupportMeasureEntry.addMaterials(
    { id: params.id },
    { externalMaterialIds: params.externalMaterialIds },
  );
  return res.data;
}

export async function setSupportMeasureEntryMaterials(params: {
  id: number;
  externalMaterialIds: number[];
}): Promise<Components.Schemas.SupportMeasureEntryDto> {
  const res = await Api.Routes.SupportMeasureEntry.setMaterials(
    { id: params.id },
    { externalMaterialIds: params.externalMaterialIds },
  );
  return res.data;
}

export async function removeMaterialFromSupportMeasureEntry(params: {
  id: number;
  externalMaterialId: number;
}): Promise<Components.Schemas.SupportMeasureEntryDto> {
  const res = await Api.Routes.SupportMeasureEntry.removeMaterial({
    id: params.id,
    externalMaterialId: params.externalMaterialId,
  });
  return res.data;
}
